import type { Node } from '@xyflow/react';
import { FinalidadeEstado, type EstadoDto, type GrupoDto, type ProcessoCartaoDto, type QuadroDto } from '@nexital/shared';
import type { NivelDetalhe } from '../../domain/camera';

export const LARGURA_CARTAO = 254;
export const ALTURA_CARTAO = 172;

/** `editandoPor`: nome de quem está arrastando ou tem a trava do item agora (RQ-COL-004). */
export type DadosGrupo = { estado: EstadoDto; grupo: GrupoDto; quantidade: number; editandoPor?: string };
export type DadosCartao = { processo: ProcessoCartaoDto; cor: string; editandoPor?: string };
export type NoGrupoTipo = Node<DadosGrupo, 'grupo'>;
export type NoCartaoTipo = Node<DadosCartao, 'cartao'>;
export type NoQuadro = NoGrupoTipo | NoCartaoTipo;

export const idGrupo = (uuid: string) => `g:${uuid}`;
export const idCartao = (uuid: string) => `c:${uuid}`;
export const uuidDoNo = (id: string) => id.slice(2);

/**
 * Converte o quadro da API em nós do React Flow. Cartões são filhos do grupo do seu estado, com posição
 * relativa ao grupo (como no banco), mas soltos: arrastados para outro grupo, mudam de estado (RQ-EST-003).
 * Cartões Sem estado ficam na bandeja, fora do quadro. `voltando` = nó que volta animado à origem.
 */
export function montarNos(dados: QuadroDto, nivel: NivelDetalhe, voltando: string | null = null): NoQuadro[] {
  const estados = new Map(dados.estados.map((e) => [e.uuid, e]));
  const grupoDoEstado = new Map(dados.grupos.map((g) => [g.estadoUuid, g]));

  const grupos: NoGrupoTipo[] = dados.grupos.map((grupo) => ({
    id: idGrupo(grupo.uuid),
    type: 'grupo',
    position: { x: grupo.x, y: grupo.y },
    width: grupo.largura,
    height: grupo.altura,
    zIndex: 0,
    data: {
      estado: estados.get(grupo.estadoUuid)!,
      grupo,
      quantidade: dados.processos.filter((p) => p.estadoUuid === grupo.estadoUuid).length,
    },
  }));

  const cartoes: NoCartaoTipo[] = dados.processos.flatMap((processo) => {
    const grupo = grupoDoEstado.get(processo.estadoUuid);
    const estado = estados.get(processo.estadoUuid);
    if (!grupo || !estado || estado.finalidade === FinalidadeEstado.SemEstado) return [];
    return [
      {
        id: idCartao(processo.uuid),
        type: 'cartao',
        parentId: idGrupo(grupo.uuid),
        className: idCartao(processo.uuid) === voltando ? 'no--voltando' : undefined,
        position: { x: processo.x, y: processo.y },
        width: LARGURA_CARTAO,
        height: ALTURA_CARTAO,
        hidden: nivel === 'minimo',
        zIndex: 1,
        data: { processo, cor: estado.cor },
      },
    ];
  });

  return [...grupos, ...cartoes];
}
